/* eslint-disable vue/one-component-per-file */
// Vue Plugins
import trackGlobalEvents from '@/mixins/tracking/track-global-events';
import store from '@/store';
import {I18n, loadLanguageAsync} from '@/utils/i18n-setup-utils';
import {createApp} from 'vue';
import VueTippy from 'vue-tippy';

// Vue Components
import CompareRackContainer from '@/components/compare/CompareRackContainer';
import CompareBar from '@/components/containers/plp/CompareBar';
import LiveChat from '@/components/LiveChat';
import SemRush from '@/components/scripts/SemRush';
import BackendProps from '@/components/BackendProps';
import TrekMarquee from '@/components/TrekMarquee';
import ImageComponent from '@/components/ImageComponent';
import RefLink from '@/components/ref-link/RefLink';
import TrekButton from '@/components/TrekButton';
import TrekFooter from '@/components/TrekFooter';
import TrekLink from '@/components/TrekLink';
import {PdlLink} from '@pedal/pdl-link';
import {PdlToast} from '@pedal/pdl-toast';
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlBadge} from '@pedal/pdl-badge';
import {PdlSwatch} from '@pedal/pdl-swatch';
import {PdlButton} from '@pedal/pdl-button';
import {PdlTabs, PdlTabPane} from '@pedal/pdl-tabs';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import TrekAvantyCheck from '@/components/scripts/TrekAvantyCheck';
import StoreDetailsScript from '@/components/scripts/StoreDetailsScript';
import StoreHoursScript from '@/components/scripts/StoreHoursScript';
import RecaptchaTokenScript from '@/components/scripts/RecaptchaTokenScript';
import SearchInput from '@/components/header-search/SearchInput';
import CartRestoration from '@/components/cart/header/CartRestoration';
import TrekListComponent from '@/components/TrekListComponent.vue';
import {PdlCallout} from '@pedal/pdl-callout';
import NotificationItem from '@/components/containers/notifications/NotificationItem.vue';
import {PdlToggleSwitch} from '@pedal/pdl-toggle-switch';
import {PdlCarousel, PdlCarouselSlide} from '@pedal/pdl-carousel';
import PlpFacetRefinementsWrapper from '@/components/containers/plp/b2c/PlpFacetRefinementsWrapper.vue';

// Vue Nav Components
import NavCategoriesLink from '@/navigation/NavCategoriesLink';
import NavUtilityBarB2bContainer from '@/navigation/containers/NavUtilityBarB2bContainer';
import NavUtilityBarControl from '@/navigation/components/NavUtilityBarControl';
import NavBar from '@/navigation/NavBar.vue';
import B2bMiniCart from '@/components/mini-cart/b2b/B2bMiniCart.vue';
import AccountSelector from '@/components/account-selector/AccountSelector';
import VendorSelector from '@/components/vendor-selector/VendorSelector';
import NotificationsNav from '@/components/containers/notifications/NotificationsNav';
import NotificationsDropdown from '@/components/containers/notifications/NotificationsDropdown';
import NavUtilityBarVendorSelector from '@/navigation/components/NavUtilityBarVendorSelector.vue';

// Vue components used site-wide
import {nonCriticalCssLoader} from '@/entryPoints/lib/non-critical-css-loader';
import emailSignup from '@/modules/EmailSignup';
import geoPrompt from '@/modules/geoPrompt';
import loadingIndicator from '@/modules/loadingIndicator';
import {PdlSectionHeader, PdlHeading, PdlSubheading} from '@pedal/pdl-section-header';

import CompareRack from '@/components/containers/plp/CompareRack';
import {PdlDialog} from '@pedal/pdl-dialog';
import {PdlDrawer} from '@pedal/pdl-drawer';

// Utils
import toggler from '@/modules/Toggler';
import {updateASMHeader} from '@/utils/asmAdjustments';
import {initInterceptors} from '@/utils/axios/toast-interceptor';

//Misc
import editorialHighlightComponent from '@/modules/editorialHighlightComponent';
import accordionComponent from '@/modules/accordionComponent.js';

//vuex store
import {mapActions} from 'vuex';
import AuthHelpers from '@/utils/auth-helpers.js';

class ShellBase {
  /**
   * Initialize a new instance of the ShellBase class.
   * @param pageComponents
   */
  constructor(pageComponents = {}, vueRouter = null) {
    this.store = store;
    this.pageComponents = pageComponents;
    this.router = vueRouter;
  }
  /**
   * Initialize Vue
   */
  async initializeVue() {
    const userEmail = store.state?.user?.userEmail;
    const customer = store.state?.user?.customer;
    const authHelpers = AuthHelpers({userEmail, customer}, store.dispatch);

    await authHelpers.handleLogin();

    this.loadPlugins();
    const app = createApp({
      components: {
        ...this.pageComponents,
        CompareBar,
        CompareRackContainer,
        BackendProps,
        TrekButton,
        PdlButton,
        TrekLink,
        TrekFooter,
        RefLink,
        NavBar,
        NavCategoriesLink,
        NavUtilityBarB2bContainer,
        NavUtilityBarControl,
        AccountSelector,
        VendorSelector,
        NotificationsNav,
        NotificationsDropdown,
        B2bMiniCart,
        PdlLink,
        PdlIcon,
        PdlBadge,
        PdlSwatch,
        PdlTabs,
        PdlTabPane,
        LiveChat,
        SemRush,
        TrekAvantyCheck,
        StoreDetailsScript,
        StoreHoursScript,
        RecaptchaTokenScript,
        SearchInput,
        ImageComponent,
        PdlSectionHeader,
        PdlHeading,
        PdlSubheading,
        TrekMarquee,
        CartRestoration,
        TrekListComponent,
        CompareRack,
        PdlDialog,
        PdlDrawer,
        PdlCallout,
        NotificationItem,
        PdlToggleSwitch,
        NavUtilityBarVendorSelector,
        PdlCarousel,
        PdlCarouselSlide,
        PdlCollapse,
        PdlCollapseItem,
        PlpFacetRefinementsWrapper,
      },

      mixins: [trackGlobalEvents],

      data() {
        return {
          isLoaded: false,
          unsubscribe: undefined,
          mobileMenuOpened: false,
        };
      },
      beforeMount() {
        loadLanguageAsync(window.ACC.languageISO).then(() => {
          this.isLoaded = true;
        });
      },
      created() {
        window.addEventListener('resize', this.handleResize);
        this.getCurrentBreakpoint();
        // authHelpers.initSelectedRetailer();
        // authHelpers.initSelectedPostcode();
      },

      beforeUnmount() {
        this.unsubscribe();
      },

      unmounted() {
        window.removeEventListener('resize', this.handleResize);
      },
      methods: {
        ...mapActions('viewSize', ['handleResize', 'getCurrentBreakpoint']),
        toast(options) {
          return this.$notify(options);
        },
      },
    });

    initInterceptors(PdlToast);
    app.config.globalProperties.$notify = PdlToast;
    app.config.devtools = true;
    app.config.performance = true;

    app.config.silet = true;
    app.use(store);
    app.use(I18n);
    app.use(VueTippy, {
      directive: 'tippy', // => v-tippy
      component: 'tippy', // => <tippy/>
    });

    if (this.router) {
      app.use(this.router);
      this.router.isReady().then(() => {
        app.mount('#app');
      });
    } else {
      app.mount('#app');
    }

    window.store = store;
    window.vm = app;
    if (!store.state.backend?.isB2Bsite) {
      authHelpers.initSelectedRetailer();
      authHelpers.initSelectedLocation();
    }
  }

  loadPlugins() {
    // Load common plugins here
  }

  preLoadInits() {
    nonCriticalCssLoader();
  }

  globalDocumentReadyInits() {
    // adding a global loading indicator
    window.loadingIndicator = loadingIndicator();

    // ASM Adjustments
    updateASMHeader();

    // Collapsing blocks handler
    const togglers = $('[data-toggler]');
    togglers.each((i, t) => {
      toggler($(t));
    });

    // Geo Prompt
    if ($('.geo-prompt-modal').length) {
      geoPrompt();
    }

    // Footer Email Sign Up Script
    emailSignup();
  }

  globalOnLoadInits() {
    $(window).trigger('resize');

    // Footer hide shop links in kiosk mode
    if ($('#isKioskMode').length) {
      $('#footerRetailerLink').parent().hide();
      $('#retailersFooterLinkElectra').parent().hide();
    }

    //Check if editorialHighLightComponet is present in page
    if ($('.js-editorial-highlight-component').length) {
      editorialHighlightComponent();
    }

    //Check if accordionComponent is present on page
    if ($('.accordion-component').length) {
      accordionComponent();
    }
  }

  loadTippy() {
    return;
  }
  unmountOldApps(key = 'default') {
    if (window.reMountedApps?.[key]?.length) {
      for (let reMountedApp of window.reMountedApps[key]) {
        reMountedApp.unmount();
      }
    }
    if (!window.reMountedApps) {
      window.reMountedApps = {};
    }
    window.reMountedApps[key] = [];
  }
  reMountVue(domElements, remountComponents, key = 'default') {
    if (!window.reMountedApps) {
      window.reMountedApps = {};
    }
    domElements.forEach((el) => {
      // eslint-disable-next-line vue/one-component-per-file
      let app = createApp({
        components: remountComponents,
        data() {
          return {
            isLoaded: false,
          };
        },
        beforeMount() {
          loadLanguageAsync(window.ACC.languageISO).then(() => {
            this.isLoaded = true;
          });
        },
      });
      app.use(store);
      app.use(I18n);
      app.use(VueTippy, {
        directive: 'tippy', // => v-tippy
        component: 'tippy', // => <tippy/>
      });
      app.mount(el);
      window.reMountedApps[key].push(app);
    });
  }
}

export {ShellBase};
